import type { Point } from '../../../types';
import type {
  DrawShapeStyle,
  Size,
  Bounds,
  Handle,
  HandlePosition,
  DrawShapeType,
} from './types';
import { getViewportScale } from '../../diagram/shapes/selectionRender';

const HANDLE_SIZE = 12;

export abstract class DrawShape {
  readonly id: string;
  abstract readonly type: DrawShapeType;
  position: Point;
  size: Size;
  style: DrawShapeStyle;
  selected: boolean;

  constructor(
    id: string,
    position: Point,
    size: Size,
    style: DrawShapeStyle
  ) {
    this.id = id;
    this.position = position;
    this.size = size;
    this.style = style;
    this.selected = false;
  }

  abstract containsPoint(p: Point): boolean;

  abstract render(ctx: CanvasRenderingContext2D): void;

  getBounds(): Bounds {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.width,
      height: this.size.height,
    };
  }

  getCenter(): Point {
    return {
      x: this.position.x + this.size.width / 2,
      y: this.position.y + this.size.height / 2,
    };
  }

  getHandles(): Handle[] {
    const { x, y } = this.position;
    const { width, height } = this.size;

    return [
      { position: 'top-left', x, y },
      { position: 'top-right', x: x + width, y },
      { position: 'bottom-left', x, y: y + height },
      { position: 'bottom-right', x: x + width, y: y + height },
      { position: 'top', x: x + width / 2, y },
      { position: 'right', x: x + width, y: y + height / 2 },
      { position: 'bottom', x: x + width / 2, y: y + height },
      { position: 'left', x, y: y + height / 2 },
    ];
  }

  getHandleAtPoint(p: Point, scale = 1): HandlePosition | null {
    const threshold = HANDLE_SIZE / scale;
    const handles = this.getHandles();
    for (const handle of handles) {
      const dx = p.x - handle.x;
      const dy = p.y - handle.y;
      if (Math.sqrt(dx * dx + dy * dy) < threshold) {
        return handle.position;
      }
    }
    return null;
  }

  resize(handlePos: HandlePosition, newHandlePoint: Point): void {
    const bounds = this.getBounds();

    switch (handlePos) {
      case 'top-left':
        this.position.x = newHandlePoint.x;
        this.position.y = newHandlePoint.y;
        this.size.width = bounds.x + bounds.width - newHandlePoint.x;
        this.size.height = bounds.y + bounds.height - newHandlePoint.y;
        break;
      case 'top-right':
        this.position.y = newHandlePoint.y;
        this.size.width = newHandlePoint.x - bounds.x;
        this.size.height = bounds.y + bounds.height - newHandlePoint.y;
        break;
      case 'bottom-left':
        this.position.x = newHandlePoint.x;
        this.size.width = bounds.x + bounds.width - newHandlePoint.x;
        this.size.height = newHandlePoint.y - bounds.y;
        break;
      case 'bottom-right':
        this.size.width = newHandlePoint.x - bounds.x;
        this.size.height = newHandlePoint.y - bounds.y;
        break;
      case 'top':
        this.position.y = newHandlePoint.y;
        this.size.height = bounds.y + bounds.height - newHandlePoint.y;
        break;
      case 'bottom':
        this.size.height = newHandlePoint.y - bounds.y;
        break;
      case 'left':
        this.position.x = newHandlePoint.x;
        this.size.width = bounds.x + bounds.width - newHandlePoint.x;
        break;
      case 'right':
        this.size.width = newHandlePoint.x - bounds.x;
        break;
    }

    // Ensure minimum size
    if (this.size.width < 10) this.size.width = 10;
    if (this.size.height < 10) this.size.height = 10;
  }

  move(delta: Point): void {
    this.position.x += delta.x;
    this.position.y += delta.y;
  }

  renderSelection(ctx: CanvasRenderingContext2D): void {
    if (!this.selected) return;

    const scale = getViewportScale(ctx);
    const bounds = this.getBounds();
    ctx.save();
    ctx.strokeStyle = '#0066ff';
    ctx.lineWidth = 2 / scale;
    ctx.setLineDash([5 / scale, 5 / scale]);
    ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
    ctx.setLineDash([]);

    // Render handles
    const handles = this.getHandles();
    for (const handle of handles) {
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#0066ff';
      ctx.lineWidth = 2 / scale;
      ctx.beginPath();
      ctx.arc(handle.x, handle.y, (HANDLE_SIZE / 2) / scale, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }

    ctx.restore();
  }

  toJSON(): object {
    return {
      id: this.id,
      type: this.type,
      position: this.position,
      size: this.size,
      style: this.style,
      selected: this.selected,
    };
  }
}
